import React, { useState } from 'react';
import { ValidationError } from 'yup';
import clsx from 'clsx';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgMapPin from '@components/framework/icons/MapPin.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import LocationSelect from '@components/framework/locationselect/LocationSelect.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import LocationOpeningHoursEditor from '@components/RingilComponents/LocationOpeningHours/LocationOpeningHoursEditor.tsx';
import { DAY_IN_WEEK_INDEX } from '@components/RingilComponents/LocationOpeningHours/OpeningHoursDayRow.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { generateUUID } from '@utils/utils.tsx';
import { LineStop, LineStopCandidate } from '@app/pages/customer/LineEdit/types.ts';
import { normalizeOpeningHours, summarizeOpeningWeek } from '@app/pages/customer/LineEdit/detailToLineForm.ts';

interface ComponentProps {
  stop: LineStop;
  locIndex: number;
  onChange: (patch: Partial<LineStop>) => void;
  validationErrors: ValidationError[];
  dataTestIdPrefix: string;
}

/**
 * The location side of a Linka stop. Unlike the expedition card it holds a *set* of address-book
 * locations (none = any location, one = locked, several = pick one when creating the shipment), no
 * manual address and no contacts, and a weekly pattern instead of concrete timeslots.
 *
 * A separate component rather than a mode of RouteLocationCard: almost nothing of that card applies
 * (contacts, manual address, ManageTimeslot), and the expedition card is being extended in parallel.
 */
const LineStopCard: React.FC<ComponentProps> = ({ stop, locIndex, onChange, validationErrors, dataTestIdPrefix }) => {
  const { tr } = useTranslationLgs();
  const candidates = stop.candidates ?? [];
  const [referenceShown, setReferenceShown] = useState(!!stop.loadingReference);
  const openingHoursPrefix = `locations[${locIndex}].openingHours.`;
  const hasOpeningHoursErrors = (validationErrors ?? []).some(e => e.path?.startsWith(openingHoursPrefix));
  // the 7-day table is long; like the expedition timeslot, a field shows the summary and opens the week on click
  const [openingHoursShown, setOpeningHoursShown] = useState(false);
  const weekSummary = summarizeOpeningWeek(stop.openingHours, day => tr(`DayInWeek${DAY_IN_WEEK_INDEX[day]}`, day), tr('LineEdit.allDay', 'celý den'));

  const setCandidates = (next: LineStopCandidate[]) => onChange({ candidates: next });

  return (
    <Card>
      <div className={'flex flex-col gap-4'}>
        <COHeadline title={tr('LineEdit.location', 'Lokalita')} variant={'h4'} icon={<SvgMapPin />} />

        {candidates.length === 0 && (
          <div className={'text-sm text-content-secondary'} data-testid={`${dataTestIdPrefix}-anyLocation`}>
            {tr('LineEdit.anyLocationHint', 'Libovolná lokalita při vytvoření přepravy')}
          </div>
        )}

        {candidates.map((candidate, index) => (
          <div key={candidate.key} className={'flex flex-row items-center gap-2'}>
            <div className={'flex-1 min-w-0'}>
              <LocationSelect
                fullWidth
                value={candidate.locationId}
                dataTestId={`${dataTestIdPrefix}-candidate-${index}`}
                onChange={v =>
                  setCandidates(candidates.map(c => (c.key === candidate.key ? { ...c, locationId: v?.locationId ?? null, name: v?.locationName } : c)))
                }
              />
            </div>
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              tooltip={tr('LineEdit.removeLocation', 'Odebrat lokalitu')}
              onClick={() => setCandidates(candidates.filter(c => c.key !== candidate.key))}
              dataTestId={`${dataTestIdPrefix}-candidate-${index}-remove`}
            />
          </div>
        ))}

        <LinkWithIcon
          label={tr('LineEdit.addLocation', 'Přidat lokalitu')}
          icon={<SvgIconPlus />}
          onClick={() => setCandidates([...candidates, { key: generateUUID(), locationId: null }])}
          dataTestId={`${dataTestIdPrefix}-addCandidate`}
        />

        {referenceShown ? (
          <Field label={tr('CreateExpedition.referenceProNakladku', 'Reference for loading')}>
            <RingilTextArea
              value={stop.loadingReference}
              onChange={v => onChange({ loadingReference: v })}
              maxLength={1000}
              rows={1}
              className={'resize-none w-full'}
              dataTestId={`${dataTestIdPrefix}-loadingReference`}
            />
          </Field>
        ) : (
          <LinkWithIcon
            label={tr('LineEdit.addLoadingReference', 'Reference pro nakládku')}
            icon={<SvgIconPlus />}
            onClick={() => setReferenceShown(true)}
            dataTestId={`${dataTestIdPrefix}-addLoadingReference`}
          />
        )}

        <Field label={tr('LineEdit.expectedManipulation', 'Očekávaná manipulace')}>
          <div className={'flex flex-col gap-4 bg-[#e9ecef] p-4'}>
            <div className={'flex flex-col gap-1'}>
              <button
                type={'button'}
                className={'p-inputtext flex w-full cursor-pointer items-center justify-between gap-2 bg-white text-left'}
                onClick={() => setOpeningHoursShown(shown => !shown)}
                aria-expanded={openingHoursShown || hasOpeningHoursErrors}
                data-testid={`${dataTestIdPrefix}-openingHoursToggle`}>
                <span className={clsx('truncate', weekSummary.length === 0 && 'text-content-tertiary')} data-testid={`${dataTestIdPrefix}-openingHoursSummary`}>
                  {weekSummary.length > 0 ? weekSummary.join(' · ') : tr('LineEdit.noOpeningHoursField', 'Pondělí – Neděle: zavřeno')}
                </span>
                <i className={clsx('pi text-content-brand', openingHoursShown || hasOpeningHoursErrors ? 'pi-chevron-up' : 'pi-calendar')} />
              </button>
              {weekSummary.length === 0 && (
                <div className={'text-xs text-content-secondary'}>
                  {tr('LineEdit.noOpeningHours', 'Bez časového okna – čas se zadá při vytvoření přepravy')}
                </div>
              )}
            </div>
            {(openingHoursShown || hasOpeningHoursErrors) && (
              <LocationOpeningHoursEditor
                variant={'timeslot'}
                value={stop.openingHours ?? normalizeOpeningHours(null)}
                onChange={v => onChange({ openingHours: v })}
                validationErrors={validationErrors}
                validationPrefix={openingHoursPrefix}
              />
            )}
          </div>
        </Field>
      </div>
    </Card>
  );
};

export default LineStopCard;
